/**
 * Required doctor profile sections for approval.
 * Each needs at least 1 entry.
 */
export const REQUIRED_DOCTOR_SECTIONS = [
  {
    id: 'work-experience',
    label: 'Work Experience',
    key: 'workExperiences',
  },
  {
    id: 'education',
    label: 'Education',
    key: 'educations',
  },
] as const;

interface DoctorProfileShape {
  workExperiences?: unknown[] | null;
  educations?: unknown[] | null;
  licenseCertifications?: unknown[] | null;
}

export interface MissingSection {
  sectionId: string;
  label: string;
}

/**
 * Returns which required doctor profile sections are missing (empty or absent).
 */
export function getMissingDoctorSections(
  doctorProfile: DoctorProfileShape | null | undefined,
): MissingSection[] {
  if (!doctorProfile) {
    return REQUIRED_DOCTOR_SECTIONS.map((s) => ({
      sectionId: s.id,
      label: s.label,
    }));
  }

  return REQUIRED_DOCTOR_SECTIONS.filter((section) => {
    const items = doctorProfile[section.key as keyof DoctorProfileShape];
    return !items || !Array.isArray(items) || items.length === 0;
  }).map((s) => ({ sectionId: s.id, label: s.label }));
}

/**
 * Returns true if all required sections have at least one entry.
 * Use this to verify actual data even when status is 'approved' (e.g. doctor deleted info after approval).
 */
export const DOCTOR_APPROVAL_REQUIREMENTS_MESSAGE =
  'Complete your profile and submit it for approval. Registration IDs, Specializations, Headline & Summary, Work Experience, and Education are required.';

export function hasAllRequiredSections(
  doctorProfile: DoctorProfileShape | null | undefined,
  user?: { headline?: string | null; bio?: string | null } | null,
): boolean {
  return getMissingApprovalCredentials(doctorProfile, user).length === 0;
}

function hasText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function itemCount(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

/**
 * Credentials that must be present before a healthcare provider can submit for approval.
 */
export function getMissingApprovalCredentials(
  doctorProfile:
    | {
        registrationIds?: unknown[] | null;
        modern_specializations?: unknown[] | null;
        traditionalSpecializations?: unknown[] | null;
        custom_traditional_specializations?: unknown[] | null;
        headline?: string | null;
        bio?: string | null;
        professional_bio?: string | null;
        workExperiences?: unknown[] | null;
        educations?: unknown[] | null;
        user?: { headline?: string | null; bio?: string | null } | null;
      }
    | null
    | undefined,
  user?: { headline?: string | null; bio?: string | null } | null,
): MissingSection[] {
  if (!doctorProfile) {
    return [
      { sectionId: 'registration-ids', label: 'Registration IDs' },
      { sectionId: 'specializations', label: 'Specializations' },
      { sectionId: 'bio-headline-section', label: 'Headline & Summary' },
      { sectionId: 'work-experience', label: 'Work Experience' },
      { sectionId: 'education', label: 'Education' },
    ];
  }

  const missing: MissingSection[] = [];

  if (itemCount(doctorProfile.registrationIds) === 0) {
    missing.push({ sectionId: 'registration-ids', label: 'Registration IDs' });
  }

  if (
    itemCount(doctorProfile.modern_specializations) === 0 &&
    itemCount(doctorProfile.traditionalSpecializations) === 0 &&
    itemCount(doctorProfile.custom_traditional_specializations) === 0
  ) {
    missing.push({
      sectionId: 'specializations',
      label: 'Specializations',
    });
  }

  const headline =
    user?.headline || doctorProfile.user?.headline || doctorProfile.headline;
  const summary =
    user?.bio ||
    doctorProfile.user?.bio ||
    doctorProfile.bio ||
    doctorProfile.professional_bio;
  if (!hasText(headline) || !hasText(summary)) {
    missing.push({
      sectionId: 'bio-headline-section',
      label: 'Headline & Summary',
    });
  }

  if (itemCount(doctorProfile.workExperiences) === 0) {
    missing.push({ sectionId: 'work-experience', label: 'Work Experience' });
  }

  if (itemCount(doctorProfile.educations) === 0) {
    missing.push({ sectionId: 'education', label: 'Education' });
  }

  return missing;
}

/**
 * Human-readable list of missing section labels.
 * E.g. "Work Experience" or "Work Experience and Education"
 */
export function formatMissingSectionsList(missing: MissingSection[]): string {
  if (missing.length === 0) return '';
  if (missing.length === 1) return missing[0].label;
  if (missing.length === 2)
    return `${missing[0].label} and ${missing[1].label}`;
  const last = missing[missing.length - 1];
  const rest = missing.slice(0, -1);
  return `${rest.map((s) => s.label).join(', ')}, and ${last.label}`;
}
